import { useEffect, useState } from 'react'
import {
  fetchDisplayScreens,
  fetchMyIODevices,
  forgetIODevice,
  listPersonalDisplayGrants,
  renameIODevice,
  setPersonalDisplayGrant,
} from '../../api/ws'
import type { PersonalDisplayGrant, PersonalDisplayLevel } from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { DisplayScreen, IODevice } from '../../store/types'
import { VoiceDevicesSheet } from './VoiceDevicesSheet'
import { PairForm } from './PairForm'

type Segment = 'personal' | 'voice' | 'displays'

// One Devices page for every kind of device — personal (this account's phones,
// watches, browsers), shared voice satellites, and shared display screens — split
// into three segments rather than the separate "Devices" / "Voice Devices" sheets
// they used to be.
export function DevicesSheet() {
  const devices = useAppStore((s) => s.ioDevices)
  const displays = useAppStore((s) => s.displayScreens)
  const orgID = useAppStore((s) => s.organizationID)
  const orgName = useAppStore(
    (s) => s.availableOrganizations.find((o) => o.is_current)?.name,
  )
  const pendingPairCode = useAppStore((s) => s.pendingPairCode)
  const setPendingPairCode = useAppStore((s) => s.setPendingPairCode)
  const [segment, setSegment] = useState<Segment>('personal')
  const [prefillCode, setPrefillCode] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    fetchMyIODevices()
    // Household-scoped + enriched (serving host, health, what's showing) — every
    // member sees every screen, unlike the owner-scoped personal list (§B6).
    fetchDisplayScreens()
  }, [])

  // A scanned kiosk QR (/pair?code=…) opens this sheet with the code to claim:
  // jump to Displays, pre-fill the code, and consume the pending flag. The claim
  // still happens on the user's tap in the pair form (so they confirm household +
  // temporary), not silently.
  useEffect(() => {
    if (!pendingPairCode) return
    setSegment('displays')
    setPrefillCode(pendingPairCode)
    setPendingPairCode(null)
  }, [pendingPairCode, setPendingPairCode])

  const refreshPersonal = () => fetchMyIODevices()
  const refreshDisplays = () => fetchDisplayScreens()
  const handleError = (e: unknown) =>
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))

  const personal = devices.filter((d) => !d.multi_human_user)
  // Ephemeral/throwaway displays (a browser used as a temp display) group under
  // their own heading so they don't clutter the permanent screens (§B5).
  const permanentDisplays = displays.filter((d) => !d.is_temp)
  const temporaryDisplays = displays.filter((d) => d.is_temp)

  const segments: { id: Segment; label: string }[] = [
    { id: 'personal', label: `Personal${personal.length ? ` (${personal.length})` : ''}` },
    { id: 'voice', label: 'Voice' },
    { id: 'displays', label: `Displays${displays.length ? ` (${displays.length})` : ''}` },
  ]

  return (
    <div style={{ fontSize: 14 }}>
      <div style={{ padding: '12px 16px 0' }}>
        {error && (
          <div style={{ padding: '8px 12px', background: '#fee', color: '#900', borderRadius: 4, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <SegmentControl segments={segments} value={segment} onChange={setSegment} />
      </div>

      {segment === 'personal' && (
        <div style={{ padding: '4px 16px 12px' }}>
          {personal.length === 0 ? (
            <p style={{ opacity: 0.6 }}>No personal devices.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {personal.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  onError={handleError}
                  onClearError={() => setError(null)}
                  onMutated={refreshPersonal}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Voice keeps its own self-contained sheet (pairing + per-room routing). */}
      {segment === 'voice' && <VoiceDevicesSheet />}

      {segment === 'displays' && (
        <div style={{ padding: '4px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {notice && (
            <div style={{ padding: '8px 12px', background: '#e6ffed', color: '#046b2f', borderRadius: 4 }}>
              {notice}
            </div>
          )}
          <PairForm
            orgID={orgID}
            orgName={orgName}
            showTemp
            deviceKind="display"
            initialCode={prefillCode}
            onError={(e) => {
              setNotice(null)
              setError(e)
            }}
            onPaired={(name, reclaimed) => {
              setError(null)
              setNotice(reclaimed ? `Re-paired “${name}”. Its settings are kept.` : `Paired “${name}”.`)
              setPrefillCode('')
              refreshDisplays()
            }}
          />
          {displays.length === 0 ? (
            <p style={{ opacity: 0.6 }}>
              No display screens paired yet. Run <code>hearth display</code> on a screen box; it
              shows a code — enter it above.
            </p>
          ) : (
            <>
              {permanentDisplays.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {permanentDisplays.map((d) => (
                    <DisplayScreenRow
                      key={d.id}
                      screen={d}
                      onError={handleError}
                      onClearError={() => setError(null)}
                      onMutated={refreshDisplays}
                    />
                  ))}
                </ul>
              )}
              {temporaryDisplays.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.7, margin: '8px 0 2px' }}>
                    Temporary
                  </div>
                  <p style={{ fontSize: 12, opacity: 0.6, margin: '0 0 4px' }}>
                    Throwaway displays — they remove themselves once their browser is gone. You can
                    also remove one now.
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {temporaryDisplays.map((d) => (
                      <DisplayScreenRow
                        key={d.id}
                        screen={d}
                        onError={handleError}
                        onClearError={() => setError(null)}
                        onMutated={refreshDisplays}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function SegmentControl({
  segments,
  value,
  onChange,
}: {
  segments: { id: Segment; label: string }[]
  value: Segment
  onChange: (s: Segment) => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        marginBottom: 12,
        background: 'var(--fill-secondary, #f0f0f3)',
        borderRadius: 8,
        padding: 3,
      }}
    >
      {segments.map((s) => {
        const active = s.id === value
        return (
          <button
            key={s.id}
            onClick={() => onChange(s.id)}
            style={{
              flex: 1,
              fontSize: 13,
              padding: '6px 8px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--background, #fff)' : 'transparent',
              fontWeight: active ? 600 : 400,
              color: active ? 'var(--label, inherit)' : 'var(--label-secondary, #666)',
            }}
          >
            {s.label}
          </button>
        )
      })}
    </div>
  )
}

function DeviceRow({
  device,
  onError,
  onClearError,
  onMutated,
}: {
  device: IODevice
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [nameDraft, setNameDraft] = useState(device.name)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setNameDraft(device.name)
  }, [device.name])

  const nameDirty = nameDraft.trim() !== device.name

  async function saveName() {
    if (!nameDirty) return
    onClearError()
    setBusy(true)
    try {
      const resp = await renameIODevice({
        id: device.id,
        newName: nameDraft.trim(),
        formFactor: device.form_factor,
        ownerHumanUserID: device.owner_human_user_id,
        multiHumanUser: device.multi_human_user,
        multiUserOrganizationID: device.multi_user_organization_id,
      })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  const isDisplay = device.form_factor === 'display'
  const removeLabel = isDisplay ? 'Remove' : 'Forget'

  async function handleForget() {
    const confirmMsg = isDisplay
      ? `Remove "${device.name}"?\n\n` +
        'The screen will stop showing content and drop back to its pairing page. ' +
        'To use it again, pair it from the app.'
      : `Forget "${device.name}"?\n\n` +
        'The device will be soft-removed and any live session will ' +
        'disconnect immediately. Re-enrolling the same device id ' +
        'reactivates the row (the operator just re-runs the login flow).'
    if (!confirm(confirmMsg)) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      const resp = await forgetIODevice(device.id)
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  // Dim the whole row when stale (no WS connect in 14+ days). Aligns
  // visually with the auto-prune sweeper which soft-removes at 30 days
  // of silence — gives the user a warning band of "you should consider
  // forgetting this" before the sweeper acts.
  const ageMs = Date.parse(device.last_seen_at || device.created_at)
  const stale = isFinite(ageMs) && Date.now() - ageMs > 14 * 24 * 60 * 60 * 1000

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: stale ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{device.name}</div>
        <div style={{ opacity: 0.6, fontSize: 12, display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span>
            {device.form_factor}
            {device.multi_human_user ? ' · shared' : ''}
          </span>
          {device.is_temp && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#8a5a00',
                background: '#fff3d6',
                borderRadius: 4,
                padding: '1px 5px',
                opacity: 1,
              }}
            >
              temporary
            </span>
          )}
        </div>
      </div>

      <LastSeen at={device.last_seen_at} createdAt={device.created_at} stale={stale} />

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Rename</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            style={{ flex: 1, fontSize: 16 }}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button onClick={saveName} disabled={busy || !nameDirty}>
            Save
          </button>
        </div>
      </label>

      {/* A personal display can be opened to household agents by its owner. */}
      {isDisplay && !device.multi_human_user && (
        <PersonalDisplayAgents screenID={device.id} onError={onError} onClearError={onClearError} />
      )}

      <div>
        <button onClick={handleForget} disabled={busy} style={{ color: '#900' }}>
          {removeLabel}
        </button>
      </div>
    </li>
  )
}

// Owner-facing grant control for a PERSONAL display: which household agents know it
// exists ("awareness") and which may publish to it ("access"). Personal screens are
// walled off from household agents by default — this is the only way an agent gets
// in. Lazy-loaded on expand so the sheet doesn't fan out a request per screen.
function PersonalDisplayAgents({
  screenID,
  onError,
  onClearError,
}: {
  screenID: string
  onError: (e: unknown) => void
  onClearError: () => void
}) {
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [grants, setGrants] = useState<PersonalDisplayGrant[]>([])
  const [busyPos, setBusyPos] = useState<string | null>(null)

  async function load() {
    onClearError()
    try {
      const resp = await listPersonalDisplayGrants(screenID)
      if (resp?.error) {
        onError(resp.error)
        return
      }
      setGrants(resp.grants ?? [])
      setLoaded(true)
    } catch (e) {
      onError(e)
    }
  }

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && !loaded) void load()
  }

  async function setLevel(g: PersonalDisplayGrant, level: PersonalDisplayLevel) {
    if (level === g.level) return
    onClearError()
    setBusyPos(g.position_id)
    // Optimistic — reflect the choice immediately, roll back on error.
    const prev = grants
    setGrants((cur) => cur.map((x) => (x.position_id === g.position_id ? { ...x, level } : x)))
    try {
      const resp = await setPersonalDisplayGrant({ screenID, positionID: g.position_id, level })
      if (resp?.error) {
        onError(resp.error)
        setGrants(prev)
      }
    } catch (e) {
      onError(e)
      setGrants(prev)
    } finally {
      setBusyPos(null)
    }
  }

  const levels: { id: PersonalDisplayLevel; label: string }[] = [
    { id: 'none', label: 'None' },
    { id: 'awareness', label: 'Awareness' },
    { id: 'access', label: 'Access' },
  ]

  return (
    <div style={{ fontSize: 13 }}>
      <button
        onClick={toggle}
        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--tint, #06c)', cursor: 'pointer', fontSize: 13 }}
      >
        {open ? '▾' : '▸'} Agents
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 12, opacity: 0.7, margin: 0 }}>
            This is your personal screen — household agents can't use it unless you say so.{' '}
            <b>Awareness</b> lets an agent know it exists and check what's on it; <b>Access</b> also
            lets it publish. (Anyone in the room can see a wall screen regardless — “personal”
            controls who can put things on it, not who can look.)
          </p>
          {!loaded ? (
            <p style={{ fontSize: 12, opacity: 0.6, margin: 0 }}>Loading…</p>
          ) : grants.length === 0 ? (
            <p style={{ fontSize: 12, opacity: 0.6, margin: 0 }}>No agents in this household yet.</p>
          ) : (
            grants.map((g) => (
              <div
                key={g.position_id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, opacity: busyPos === g.position_id ? 0.5 : 1 }}
              >
                <span style={{ fontWeight: 500 }}>{g.agent_name}</span>
                <div style={{ display: 'flex', gap: 3, background: 'var(--fill-secondary, #f0f0f3)', borderRadius: 7, padding: 2 }}>
                  {levels.map((l) => {
                    const active = l.id === g.level
                    return (
                      <button
                        key={l.id}
                        onClick={() => setLevel(g, l.id)}
                        disabled={busyPos === g.position_id}
                        style={{
                          fontSize: 12,
                          padding: '4px 8px',
                          borderRadius: 5,
                          border: 'none',
                          cursor: 'pointer',
                          background: active ? 'var(--background, #fff)' : 'transparent',
                          fontWeight: active ? 600 : 400,
                          color: active ? 'var(--label, inherit)' : 'var(--label-secondary, #666)',
                        }}
                      >
                        {l.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// A household display screen: name + temp badge, the serving display server and
// health (host up? browser live?), what's currently showing + any TTL, and Remove.
// Household-visible — every member sees screen health, not just the claimer (§B6).
function DisplayScreenRow({
  screen,
  onError,
  onClearError,
  onMutated,
}: {
  screen: DisplayScreen
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)

  async function handleRemove() {
    if (
      !confirm(
        `Remove "${screen.name}"?\n\n` +
          'The screen will stop showing content and drop back to its pairing page. ' +
          'To use it again, pair it from the app.',
      )
    ) {
      return
    }
    onClearError()
    setBusy(true)
    try {
      const resp = await forgetIODevice(screen.id)
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  const showing = screen.expired
    ? 'nothing (content expired)'
    : screen.kind
      ? `${screen.kind}${screen.payload ? `: ${truncate(screen.payload)}` : ''}`
      : 'nothing yet'

  let health: { text: string; color: string }
  if (screen.screen_online) health = { text: 'screen live', color: '#046b2f' }
  else if (screen.host_online) health = { text: 'no browser connected', color: '#a60' }
  else health = { text: 'display server offline', color: '#900' }

  let ttl = ''
  if (screen.expires_at && !screen.expired) {
    const d = new Date(screen.expires_at)
    if (!isNaN(d.getTime())) ttl = ` · until ${d.toLocaleTimeString()}`
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        opacity: screen.host_online ? 1 : 0.6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{screen.name}</div>
        {screen.is_temp && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#8a5a00',
              background: '#fff3d6',
              borderRadius: 4,
              padding: '1px 5px',
            }}
          >
            temporary
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        {screen.serving_host_name ? `on ${screen.serving_host_name}` : 'not bound to a display server'}
        {' · '}
        <span style={{ color: health.color }}>{health.text}</span>
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        Showing: {showing}
        {ttl}
      </div>
      <div>
        <button onClick={handleRemove} disabled={busy} style={{ color: '#900' }}>
          Remove
        </button>
      </div>
    </li>
  )
}

function truncate(s: string, n = 60): string {
  return s.length > n ? s.slice(0, n) + '…' : s
}

function LastSeen({
  at,
  createdAt,
  stale,
}: {
  at?: string
  createdAt: string
  stale: boolean
}) {
  // Use last_seen_at when present; fall back to created_at so a row
  // that never connected still gets an honest "age" instead of "never".
  const ts = at || createdAt
  const label = at ? 'Last seen' : 'Never connected (created)'
  return (
    <div
      style={{
        fontSize: 12,
        color: stale ? '#a60' : undefined,
        opacity: stale ? 1 : 0.7,
      }}
    >
      {label}: {formatRelative(ts)}
      {stale && ' · stale'}
    </div>
  )
}

function formatRelative(ts: string): string {
  const ms = Date.parse(ts)
  if (!isFinite(ms)) return ts
  const diff = Date.now() - ms
  if (diff < 0) return 'in the future'
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}
